import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORIES = [
  { v: "roofing_system", en: "Roofing Systems", fr: "Systèmes de toiture" },
  { v: "siding_system", en: "Siding Systems", fr: "Systèmes de revêtement" },
  { v: "soffit_fascia", en: "Soffit & Fascia", fr: "Soffite & Fascia" },
  { v: "roof_flashing", en: "Roof Flashing & Custom Metal", fr: "Solins & Métal sur mesure" },
  { v: "siding_trim", en: "Siding Flashing & Trim", fr: "Solins & Moulures revêtement" },
  { v: "underlayment", en: "Underlayment & Membranes", fr: "Sous-couches & Membranes" },
  { v: "deck_repair", en: "Deck Repair — Wood & Plywood", fr: "Réparation de pontage — Bois & Contreplaqué" },
  { v: "fasteners", en: "Fasteners & Nails", fr: "Attaches & Clous" },
  { v: "sealants", en: "Sealants & Caulking", fr: "Scellants & Calfeutrage" },
  { v: "ventilation", en: "Ventilation", fr: "Ventilation" },
  { v: "gutters", en: "Gutters & Drainage", fr: "Gouttières & Drainage" },
  { v: "snow_ice", en: "Snow & Ice Management", fr: "Gestion neige & glace" },
  { v: "penetrations", en: "Skylights / Pipes / Chimneys", fr: "Puits de lumière / Évents / Cheminées" },
  { v: "surcharges", en: "Surcharges", fr: "Suppléments" },
  { v: "safety_equipment", en: "Safety / Setup / Rentals", fr: "Sécurité / Installation / Location" },
  { v: "service", en: "Service / Labor / Travel", fr: "Service / Main-d'œuvre / Déplacement" },
  { v: "custom_metal", en: "Custom Bent Metal", fr: "Métal plié sur mesure" },
  { v: "labor", en: "Labor", fr: "Main-d'œuvre" },
  { v: "other", en: "Other", fr: "Autre" },
];
const UNITS = ["sqft", "linft", "unit", "hour", "lot", "ea"];

export default function Catalog() {
  const { t, lang } = useI18n();
  const { data: items = [] } = useSWR("/catalog", fetcher);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", label_fr: "", category: "roofing_system", unit: "sqft", unit_price: 0, notes: "", active: true });
  const [filter, setFilter] = useState("");

  const grouped = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = f ? items.filter((it) => (it.label || "").toLowerCase().includes(f) || (it.label_fr || "").toLowerCase().includes(f)) : items;
    const g = {};
    list.forEach((it) => { (g[it.category] ||= []).push(it); });
    return g;
  }, [items, filter]);

  const visibleCategories = useMemo(
    () => CATEGORIES.filter((c) => grouped[c.v]),
    [grouped]
  );

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      label: item.label, label_fr: item.label_fr || "",
      category: item.category, unit: item.unit,
      unit_price: item.unit_price, notes: item.notes || "", active: item.active,
    });
    setOpen(true);
  };

  const startNew = () => {
    setEditing(null);
    setForm({ label: "", label_fr: "", category: "roofing_system", unit: "sqft", unit_price: 0, notes: "", active: true });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.label) return toast.error(lang === "fr" ? "Étiquette requise" : "Label required");
    try {
      const payload = { ...form, unit_price: Number(form.unit_price) };
      if (editing) await api.patch(`/catalog/${editing.id}`, payload);
      else await api.post("/catalog", payload);
      toast.success(lang === "fr" ? "Enregistré" : "Saved");
      setOpen(false);
      mutate("/catalog");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cet article ?" : "Delete this item?")) return;
    await api.delete(`/catalog/${id}`);
    mutate("/catalog");
  };

  const resetDefaults = async () => {
    if (!window.confirm(lang === "fr"
      ? "Réinitialiser au tarif C.H.C. par défaut ? Vos modifications seront perdues."
      : "Reset to default C.H.C. price list? Your edits will be lost.")) return;
    await api.post("/catalog/reset");
    toast.success(lang === "fr" ? "Catalogue réinitialisé" : "Catalog reset");
    mutate("/catalog");
  };

  const toggleActive = async (it) => {
    await api.patch(`/catalog/${it.id}`, { active: !it.active });
    mutate("/catalog");
  };

  return (
    <div data-testid="catalog-page">
      <PageHeader
        eyebrow="// 12 / Catalog"
        title={lang === "fr" ? "Catalogue de prix" : "Price Catalog"}
        sub={lang === "fr"
          ? "Votre liste maître C.H.C. — utilisée dans l'estimateur et les soumissions. Cliquez n'importe quel prix pour le modifier."
          : "Your master C.H.C. price list — used in Estimator and Proposals. Click any item to edit its price."}
        action={
          <div className="flex items-center gap-3">
            <Button data-testid="catalog-reset" onClick={resetDefaults} variant="outline" className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-wider text-xs h-11">
              <ArrowCounterClockwise size={14} className="mr-2" /> {lang === "fr" ? "Restaurer" : "Reset defaults"}
            </Button>
            <Button data-testid="catalog-new" onClick={startNew} className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
              <Plus size={14} className="mr-2" weight="bold" /> {lang === "fr" ? "Nouvel article" : "New item"}
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <Input data-testid="catalog-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={lang === "fr" ? "Filtrer par nom…" : "Filter by name…"} className="rounded-none max-w-md" />
          <span className="mono text-xs text-zinc-500">{items.length} {lang === "fr" ? "articles au total" : "items total"}</span>
        </div>

        {items.length === 0 ? (
          <EmptyState title={lang === "fr" ? "Catalogue vide" : "Catalog is empty"} sub={lang === "fr" ? "Ajoutez un article ou restaurez la liste par défaut" : "Add an item or restore defaults"} />
        ) : (
          <div className="space-y-8">
            {visibleCategories.map((cat) => (
              <section key={cat.v} className="border border-zinc-200">
                <header className="border-b border-zinc-200 px-5 py-3 bg-zinc-50">
                  <p className="label-eyebrow text-orange-600">{lang === "fr" ? cat.fr : cat.en}</p>
                  <p className="mono text-xs text-zinc-500 mt-1">{grouped[cat.v].length} {lang === "fr" ? "articles" : "items"}</p>
                </header>
                <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500">
                  <div className="col-span-7">{lang === "fr" ? "Article" : "Item"}</div>
                  <div className="col-span-1 text-right">Unit</div>
                  <div className="col-span-2 text-right">{lang === "fr" ? "Prix" : "Price"}</div>
                  <div className="col-span-1 text-center">Active</div>
                  <div className="col-span-1"></div>
                </div>
                {grouped[cat.v].map((it) => (
                  <div key={it.id} data-testid={`catalog-row-${it.id}`} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm hover:bg-zinc-50">
                    <button onClick={() => startEdit(it)} className="col-span-7 text-left">
                      <div className="font-semibold">{lang === "fr" && it.label_fr ? it.label_fr : it.label}</div>
                      {it.notes && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{it.notes}</div>}
                    </button>
                    <div className="col-span-1 text-right mono text-xs text-zinc-500">/{it.unit}</div>
                    <button onClick={() => startEdit(it)} className="col-span-2 text-right mono font-bold text-orange-600 hover:underline">
                      {fmt$(it.unit_price)}
                    </button>
                    <div className="col-span-1 text-center"><Switch data-testid={`catalog-active-${it.id}`} checked={it.active} onCheckedChange={() => toggleActive(it)} /></div>
                    <div className="col-span-1 text-right">
                      <button data-testid={`catalog-delete-${it.id}`} onClick={() => remove(it.id)} className="text-zinc-400 hover:text-orange-600"><Trash size={14} /></button>
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none border-zinc-300 max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl tracking-tight">
                {editing ? (lang === "fr" ? "Modifier l'article" : "Edit item") : (lang === "fr" ? "Nouvel article" : "New item")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              <Field label={lang === "fr" ? "Étiquette (EN)" : "Label (EN)"}>
                <Input data-testid="cat-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={lang === "fr" ? "Étiquette (FR)" : "Label (FR)"}>
                <Input data-testid="cat-label-fr" value={form.label_fr} onChange={(e) => setForm({ ...form, label_fr: e.target.value })} className="rounded-none" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="cat-category" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{lang === "fr" ? c.fr : c.en}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Unit">
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="cat-unit" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={lang === "fr" ? "Prix" : "Price"}>
                  <Input data-testid="cat-price" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="rounded-none mono" />
                </Field>
              </div>
              <Field label={t("common.notes")}>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" />
              </Field>
              <Button data-testid="cat-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
